/* eslint-disable @typescript-eslint/naming-convention */

import type {
  PaletteMode,
  Theme,
  ThemeOptions,
} from '@mui/material';
import {
  lighten,
  createTheme as muiCreateTheme,
  tableCellClasses,
} from '@mui/material';
import '@gen-epix/ui-client-casedb/types';

import roSansBoldWoff from './fonts/RO-SansWebText-Bold.woff';
import roSansItalicWoff from './fonts/RO-SansWebText-Italic.woff';
import roSansRegularWoff from './fonts/RO-SansWebText-Regular.woff';
import roSansBoldTtf from './fonts/RO-SansWebText-Bold.ttf';
import roSansItalicTtf from './fonts/RO-SansWebText-Italic.ttf';
import roSansRegularTtf from './fonts/RO-SansWebText-Regular.ttf';
import notoSansMonoWoff2 from './fonts/NotoSansMono.woff2';

export const createTheme = (paletteMode: PaletteMode): Theme => {
  const MuiCssBaselineStyleOverrides = `
      *:focus-visible, .Mui-focusVisible {
        outline: 2px dotted #000 !important;
        outline-offset: 0 !important;
        box-shadow: 0 0 0 2px #fff !important;
      }

      html, body {
        font-size: 14px;
        height: 100%;
        overscroll-behavior: none;
      }
      #root {
        height: 100%;
      }
      @font-face {
        font-family: "RO Sans";
        font-style: normal;
        font-weight: 400;
        src: url(${roSansRegularWoff}) format("woff"), url(${roSansRegularTtf}) format("truetype")
      }

      @font-face {
        font-family: "RO Sans";
        font-style: italic;
        font-weight: 400;
        src: url(${roSansItalicWoff}) format("woff"), url(${roSansItalicTtf}) format("truetype")
      }

      @font-face {
        font-family: "RO Sans";
        font-style: normal;
        font-weight: 700;
        src: url(${roSansBoldWoff}) format("woff"), url(${roSansBoldTtf}) format("truetype")
      }

      /* cyrillic-ext */
      @font-face {
        font-family: 'Noto Sans Mono';
        font-style: normal;
        font-weight: 100 900;
        font-stretch: 100%;
        font-display: swap;
        src: url(${notoSansMonoWoff2}) format('woff2');
        unicode-range: U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F;
      }
      /* cyrillic */
      @font-face {
        font-family: 'Noto Sans Mono';
        font-style: normal;
        font-weight: 100 900;
        font-stretch: 100%;
        font-display: swap;
        src: url(${notoSansMonoWoff2}) format('woff2');
        unicode-range: U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;
      }
      /* greek-ext */
      @font-face {
        font-family: 'Noto Sans Mono';
        font-style: normal;
        font-weight: 100 900;
        font-stretch: 100%;
        font-display: swap;
        src: url(${notoSansMonoWoff2}) format('woff2');
        unicode-range: U+1F00-1FFF;
      }
      /* greek */
      @font-face {
        font-family: 'Noto Sans Mono';
        font-style: normal;
        font-weight: 100 900;
        font-stretch: 100%;
        font-display: swap;
        src: url(${notoSansMonoWoff2}) format('woff2');
        unicode-range: U+0370-0377, U+037A-037F, U+0384-038A, U+038C, U+038E-03A1, U+03A3-03FF;
      }
      /* vietnamese */
      @font-face {
        font-family: 'Noto Sans Mono';
        font-style: normal;
        font-weight: 100 900;
        font-stretch: 100%;
        font-display: swap;
        src: url(${notoSansMonoWoff2}) format('woff2');
        unicode-range: U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+0300-0301, U+0303-0304, U+0308-0309, U+0323, U+0329, U+1EA0-1EF9, U+20AB;
      }
      /* latin-ext */
      @font-face {
        font-family: 'Noto Sans Mono';
        font-style: normal;
        font-weight: 100 900;
        font-stretch: 100%;
        font-display: swap;
        src: url(${notoSansMonoWoff2}) format('woff2');
        unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
      }
      /* latin */
      @font-face {
        font-family: 'Noto Sans Mono';
        font-style: normal;
        font-weight: 100 900;
        font-stretch: 100%;
        font-display: swap;
        src: url(${notoSansMonoWoff2}) format('woff2');
        unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
      }
    `;


  const themeOptions: ThemeOptions = {
    ...(process.env.NODE_ENV === 'test' && { transitions: { create: () => 'none' } }),
    breakpoints: {
      values: {
        lg: 1340,
        md: 1024,
        sm: 640,
        xl: 1920,
        xs: 0,
      },
    },
    components: {
      GenEpixUiClientCasedbLineList: {
        font: '0.8rem "Noto Sans Mono"',
      },
      GenEpixUiClientCommonFooter: {
        background: '#154273',
        color: '#fff',
        sectionBorderColor: '#fff',
      },
      GenEpixUiClientCommonNavbar: {
        activeBackground: '#fff',
        activeColor: '#154273',
        background: '#154273',
        environmentMessageColor: '#ffea00',
        primaryColor: '#fff',
        secondaryColor: '#ddeff8',
      },
      GenEpixUiPhylogeneticTree: {
        backgroundColor: '#fff',
        defaultProps: {
          dimFn: (color: string) => lighten(color, 0.8),
        },
        labelFont: 'bold 0.7rem "Noto Sans Mono"',
        regularFillColorSupportLine: '#E0E6F1',
        scaleColor: '#000',
        scaleFont: 'bold 0.7rem "Noto Sans Mono"',
        supportLineColorLinked: '#000',
        supportLineColorUnlinked: lighten('#000', 0.75),
        treeColor: '#000',
      },
      MuiAlertTitle: {
        styleOverrides: {
          root: {
            color: 'inherit',
            fontWeight: 'bold',
            marginBottom: 0,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          color: 'primary',
          variant: 'contained',
        },
        styleOverrides: {
          root: {
            fontWeight: 'bold',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: MuiCssBaselineStyleOverrides,
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            backgroundColor: '#8fcae7',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            fontWeight: 'bold',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            color: '#282245',
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              color: '#b92717',
            },
            '&.Mui-warning': {
              color: 'rgb(118,79,20)',
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              color: '#b92717',
            },
            '&.Mui-warning': {
              color: 'rgb(118,79,20)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-error': {
              color: '#b92717',
            },
            '&.Mui-warning': {
              color: 'rgb(118,79,20)',
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecorationColor: 'inherit',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& input': {
              padding: '7.5px 4px 7.5px 5px',
            },
            padding: '9px',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '7.5px 32px 7.5px 5px',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: '#8fcae7',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            [`& .${tableCellClasses.root}`]: {
              fontWeight: 700,
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          enterDelay: 500,
          enterNextDelay: 500,
        },
      },
      MuiTypography: {
        styleOverrides: {
          body1: {
            color: 'rgba(0,0,0,0.87)',
            fontSize: '1rem', // 14
          },
          body2: {
            color: 'rgba(0,0,0,0.87)',
            fontSize: '1rem', // 14
          },
          caption: {
            fontStyle: 'italic',
          },
          h1: {
            color: '#154273',
            fontSize: '2.143rem', // 30
            fontWeight: 700,
          },
          h2: {
            color: '#154273',
            fontSize: '1.857rem', // 26
            fontWeight: 400,
          },
          h3: {
            color: '#154273',
            fontSize: '1.714rem', // 24
            fontWeight: 700,
          },
          h4: {
            color: '#154273',
            fontSize: '1.429rem', // 20
            fontWeight: 700,
          },
          h5: {
            color: '#154273',
            fontSize: '1.286rem', // 16
            fontWeight: 700,
          },
          h6: {
            color: '#154273',
            fontSize: '1.143rem', // 16
            fontWeight: 400,
          },
          subtitle1: {
            color: '#154273',
            fontSize: '1.286rem', // 18
            fontWeight: 400,
          },
          subtitle2: {
            color: '#154273',
            fontSize: '1.143rem', // 16
            fontWeight: 700,
          },
        },
      },
    },
    palette: {
      divider: '#eeeeee',
      error: {
        contrastText: '#fff',
        main: '#cf0101',
      },
      grey: {
        100: '#eeeeee',
        500: '#777777',
      },
      mode: paletteMode,
      primary: {
        contrastText: '#fff',
        dark: '#154273',
        light: '#ddeff8',
        main: '#007bc7',
      },
      secondary: {
        contrastText: '#fff',
        dark: '#990046',
        light: '#df669d',
        main: '#ca005d',
      },
      success: {
        contrastText: '#fff',
        main: '#05B803',
      },
      text: {
        disabled: '#767676',
        primary: '#000',
      },
    },
    shape: {
      borderRadius: 0,
    },
    spacing: 8,
    typography: {
      fontFamily: 'RO Sans',
      fontSize: 10,
      htmlFontSize: 10,
    },
  };

  return muiCreateTheme(themeOptions);
};
